/**
 * Property Rear Form - Schema-Based Version
 * This is a simplified version using the BuiltInFormRenderer
 * Requires minimal code - just state management and data flow
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { UseAutosaveReturn } from '../../hooks/useAutosave';
import PropertyRearIconSrc from '../../assets/B Background Removed.png';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import { propertyRearFormSchema } from '../../schemas/propertyRearFormSchema';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

export interface PropertyRearFormProps {
  onNavigateToRooms: () => void;
  onNavigateBackToPropertyFront: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  autosave?: UseAutosaveReturn;
  onSaveImmediately?: () => Promise<string>;
  onGalleryStateChange?: (isOpen: boolean) => void;
  customFields?: FieldSchema[];
  hiddenFields?: string[];
  hiddenSections?: string[];
  sectionOrder?: string[];
  builtInFieldOrder?: string[];
}

const PropertyRearFormSchemaVersion: React.FC<PropertyRearFormProps> = ({
  onNavigateToRooms,
  onNavigateBackToPropertyFront,
  surveyData,
  surveyId,
  floorPlanId,
  onDataUpdate,
  autosave,
  onSaveImmediately,
  onGalleryStateChange,
  customFields = [],
  hiddenFields = [],
  hiddenSections = [],
  sectionOrder = [],
  builtInFieldOrder = []
}) => {
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  // State - simple key-value stores
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<Record<string, any[]>>({});
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);

  const migrationRunOnceRef = useRef(false);
  const isInternalUpdate = useRef(false);

  // Load data from surveyData
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRear] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.property_rear) {
      const rearData = surveyData.property_rear as any;
      
      // Separate photos from regular form data
      const photoFields = new Set([
        'rearElevationImage',
        'roofTypeEvidenceImages',
        'rearBackyardImages',
        'gutteringDrainageImages',
        'wallTypeEvidenceImage',
        'wallDefectImages'
      ]);
      
      const newFormData: Record<string, any> = {};
      const newPhotos: Record<string, any[]> = {};
      
      Object.entries(rearData).forEach(([key, value]) => {
        if (photoFields.has(key)) {
          // Ensure photo data is array
          newPhotos[key] = Array.isArray(value) ? value : (value ? [value] : []);
        } else {
          newFormData[key] = value;
        }
      });
      
      setFormData(newFormData);
      
      // 🔧 FLICKER FIX: Use optimistic updates for photos
      // Only replace photos if current state is empty or we have new photos from server
      setPhotos(prev => {
        const hasActiveUploads = Object.values(prev).some(photoArray => 
          Array.isArray(photoArray) && photoArray.some(p => p.uploading === true)
        );
        
        // If photos are actively uploading, keep current state to prevent flicker
        if (hasActiveUploads) {
          console.log('[PropertyRear] 📸 Keeping current photo state - uploads in progress');
          return prev;
        }
        
        // Otherwise, use new photos from surveyData
        return newPhotos;
      });
    }
  }, [surveyData?.property_rear]);

  // Load custom field values
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRear] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.property_rear && customFields && customFields.length > 0) {
      const existingData: Record<string, any> = {};
      customFields.forEach(field => {
        // Load the field value
        const value = (surveyData.property_rear as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
        
        // Also load photos if they exist (stored with _photos suffix)
        const photosFieldId = `${field.id}_photos`;
        const photoValue = (surveyData.property_rear as any)?.[photosFieldId];
        if (photoValue !== undefined) {
          existingData[photosFieldId] = photoValue;
        }
      });
      if (Object.keys(existingData).length > 0) {
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.property_rear, customFields]);

  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRear] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);

  // Photo migration to Supabase (if needed)
  useEffect(() => {
    if (!photos || Object.keys(photos).length === 0) return;
    if (migrationRunOnceRef.current) return;
    
    let needsMigration = false;
    Object.values(photos).forEach(photoArray => {
      if (!Array.isArray(photoArray)) return;
      photoArray.forEach(photo => {
        if ((photo.dataUrl || photo.base64String) && (!photo.url || photo.url === '')) {
          needsMigration = true;
        }
      });
    });
    
    if (!needsMigration) return;
    
    migrationRunOnceRef.current = true;
    
    Object.entries(photos).forEach(([photoType, photoArray]) => {
      if (!Array.isArray(photoArray)) return;
      
      photoArray.forEach(photo => {
        if (!photo.dataUrl && !photo.base64String) return;
        if (photo.url && photo.url !== '') return;
        
        const base64Data = photo.dataUrl || photo.base64String || '';
        queuePhotoUpload(
          photo.displayId,
          base64Data,
          'property_rear',
          photoType,
          async (url: string) => {
            setPhotos(current => ({
              ...current,
              [photoType]: current[photoType].map(p => 
                p.displayId === photo.displayId 
                  ? { 
                      displayId: p.displayId, 
                      format: p.format, 
                      url, 
                      uploading: false, 
                      error: undefined,
                      // Keep dataUrl temporarily for display until CDN confirms
                      dataUrl: p.dataUrl,
                      base64String: p.base64String
                    }
                  : p
              )
            }));
            
            // Remove base64 data after a short delay to ensure CDN URL is working
            setTimeout(() => {
              setPhotos(current => ({
                ...current,
                [photoType]: current[photoType].map(p =>
                  p.displayId === photo.displayId
                    ? { displayId: p.displayId, format: p.format, url, uploading: false, error: undefined }
                    : p
                )
              }));
            }, 500);
          },
          (error: string) => {
            console.error(`Migration upload failed for ${photoType}:`, error);
          }
        );
      });
    });
  }, [photos, queuePhotoUpload]);

  // Handle form field changes
  const handleFormDataChange = (fieldId: string, value: any) => {
    // Check if this is a photo field
    const isPhotoField = fieldId.endsWith('_photos') || fieldId.endsWith('Image') || fieldId.endsWith('Images');
    
    // If it's a photo field, also update photos state
    if (isPhotoField) {
      setPhotos(prev => ({ ...prev, [fieldId]: value }));
    }
    
    setFormData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          const photosToSave = isPhotoField ? { ...photos, [fieldId]: value } : photos;
          onDataUpdate(prevData => ({
            ...prevData,
            property_rear: {
              ...updated,
              ...photosToSave,
              ...customFieldData
            } as any
          }), 'property_rear');
        }, 50);
      }
      
      return updated;
    });
  };

  // Handle photo changes
  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    const newPhotoData = newPhotos.map(photo => {
      // Ensure dataUrl is properly formatted
      let dataUrl = photo.dataUrl || photo.base64String;
      // If it's a raw base64 string (no data: prefix), format it properly
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      return {
        displayId: `${Date.now()}_${Math.random()}`,
        dataUrl,
        format: photo.format || 'jpeg',
        uploading: true
      };
    });
    
    setPhotos(prev => {
      const updated = {
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...newPhotoData]
      };
      
      // Queue for upload
      newPhotoData.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'property_rear',
          fieldId,
          async (url: string) => {
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p => 
                p.displayId === photo.displayId 
                  ? { displayId: p.displayId, format: p.format, url, uploading: false }
                  : p
              )
            }));
          },
          (error: string) => {
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p => 
                p.displayId === photo.displayId 
                  ? { ...p, uploading: false, error }
                  : p
              )
            }));
          }
        );
      });
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate(prevData => ({
            ...prevData,
            property_rear: {
              ...formData,
              ...updated,
              ...customFieldData
            } as any
          }), 'property_rear');
        }, 50);
      }
      
      return updated;
    });
  };

  // Handle custom field changes
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate(prevData => ({
            ...prevData,
            property_rear: {
              ...formData,
              ...photos,
              ...updated
            } as any
          }), 'property_rear');
        }, 50);
      }
      
      return updated;
    });
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Property Rear Form Data:', { formData, photos, customFieldData });
    onNavigateToRooms();
  };

  // Gallery handlers
  const handleGalleryOpen = (fieldId: string) => {
    setSelectedGallery(fieldId);
    if (onGalleryStateChange) onGalleryStateChange(true);
  };

  const handleGalleryClose = () => {
    setSelectedGallery(null);
    if (onGalleryStateChange) onGalleryStateChange(false);
  };

  // Remove photo function (like PropertyFront)
  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    console.log(`[PropertyRear] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    // Find the photo to delete from storage
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => p.displayId === displayIdToRemove);
    
    if (photoToDelete) {
      let filePathToDelete = photoToDelete.fileName;
      
      // If no fileName but has URL, try to extract path from CDN URL
      if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
        const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
        if (urlParts) {
          const [bucket, ...pathParts] = urlParts.split('/');
          filePathToDelete = pathParts.join('/');
          console.log(`[PropertyRear] 🗑️ Extracted path from URL: ${filePathToDelete}`);
        }
      }
      
      if (filePathToDelete) {
        try {
          console.log(`[PropertyRear] 🗑️ Deleting from storage: ${filePathToDelete}`);
          await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
          console.log(`[PropertyRear] ✅ Deleted from storage`);
        } catch (error) {
          console.warn(`[PropertyRear] ⚠️ Failed to delete from storage:`, error);
        }
      } else {
        console.log(`[PropertyRear] ℹ️ No fileName/URL found, skipping storage deletion (local photo only)`);
      }
    }
    
    // Remove from state
    const filteredPhotos = currentPhotos.filter((p: any) => p.displayId !== displayIdToRemove);
    console.log(`[PropertyRear] ✅ Removed photo from state, ${filteredPhotos.length} remaining in ${photoType}`);
    
    // Update photos state
    setPhotos(prev => ({
      ...prev,
      [photoType]: filteredPhotos
    }));
    
    // Also update formData state (for consistency)
    setFormData(prev => ({
      ...prev,
      [photoType]: filteredPhotos
    }));
    
    // Trigger immediate autosave with updated photos
    if (onDataUpdate) {
      setTimeout(() => {
        isInternalUpdate.current = true;
        onDataUpdate(prevData => ({
          ...prevData,
          property_rear: {
            ...formData,
            ...photos,  // Keep all other photo fields
            [photoType]: filteredPhotos,  // Override with updated field
            ...customFieldData
          } as any
        }), 'property_rear');
      }, 50);
    }
  };

  // Combine all data for form value handling
  const allFormData = { ...formData, ...customFieldData };

  return (
    <>
      <BuiltInFormRenderer
        schema={propertyRearFormSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          // Check if it's a custom field (strip _photos suffix for comparison)
          const baseFieldId = fieldId.replace(/_photos$/, '');
          if (customFields.some(f => f.id === baseFieldId)) {
            handleCustomFieldChange(fieldId, value);
          } else {
            handleFormDataChange(fieldId, value);
          }
        }}
        onPhotosChange={handlePhotosChange}
        onSubmit={handleSubmit}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={hiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: {
            label: '← Previous',
            onClick: onNavigateBackToPropertyFront
          },
          next: {
            label: 'Next: Rooms →',
            onClick: onNavigateToRooms
          }
        }}
        onGalleryOpen={handleGalleryOpen}
        selectedGallery={selectedGallery}
        formIcon={<img src={PropertyRearIconSrc} alt="Rear" className="w-8 h-8" />}
      />

      {/* Photo Gallery Modal */}
      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl,
            localPath: photo.dataUrl
          }))}
          sectionTitle={selectedGallery}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(prev => ({
              ...prev,
              [selectedGallery]: prev[selectedGallery].map(p => 
                p.displayId === photoId ? { ...p, note } : p
              )
            }));
          }}
        />
      )}
    </>
  );
};

export default PropertyRearFormSchemaVersion;
